import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { whyChoose } from "@/lib/constants";
import { FadeUp } from "@/lib/animations/components";
import { AnimatedCounter } from "@/lib/utils";

export function AboutOverviewSection() {
  return (
    <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] noise-overlay" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl lg:sticky lg:top-28 self-start"
          >

            <h2 className="text-4xl md:text-5xl font-bold leading-tight max-w-xl mb-6">
              A firm built on trust,
              <br />
              <span>grounded in expertise.</span>
            </h2>

            <div className="space-y-4 text-lg text-neutral-700 leading-relaxed">
              <p>
                Rosewood Consulting is a dedicated tax and advisory firm helping
                businesses and individuals across Kenya stay compliant, manage
                risk, and make confident financial decisions.
              </p>
              <p>
                We translate complex tax law into clear, practical strategies —
                protecting your business today while positioning it for
                sustainable growth tomorrow.
              </p>
            </div>

                  <FadeUp delay={1.1} className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-border/50">
                    <span className="text-neutral-600">Trusted by</span>
                      <span className="text-gold font-bold">
                        <AnimatedCounter end={50} suffix="+" duration={1.8} />
                      </span>
                      <span className="text-neutral-600">businesses across Kenya</span>
                    </div>
              
                    
                  </FadeUp>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-burgundy text-white font-semibold rounded-xl hover:bg-burgundy-dark transition-colors"
              >
                More about us
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-7 py-3.5 border-2 border-border text-burgundy font-semibold rounded-xl hover:border-burgundy hover:bg-burgundy/5 transition-all"
              >
                Get in touch
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyChoose.slice(0, 4).map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30, scale: 0.97 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.45,
                    delay: i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{
                    y: -5,
                    transition: { type: "spring", stiffness: 300, damping: 15 },
                  }}
                  className="relative group bg-cream/80 backdrop-blur-sm rounded-2xl p-5 border border-border/50 hover:border-gold/30 hover:bg-gold transition-colors hover:text-white"
                >
                  <div className="absolute top-0 left-3 right-3 h-[2px] bg-gradient-to-r from-transparent via-gold/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <h3 className="text-base font-semibold mb-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed">
                    {item.description}
                  </p>

                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-gold/10 rounded-full blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

    </section>
  );
}